import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type TimeCardEntryVM from 'ab-viewModels/timeSheet/timeCardEntry.viewModel';
import type TimeCardRejectedTimeSheetVM from 'ab-viewModels/timeSheet/timeCardRejectedTimeSheet.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';
import { shouldEnableFeatureFromDate } from '@acceligentllc/shared/utils/timeSheet';
import type * as TimeSheetEntryUtils from '@acceligentllc/shared/utils/timeSheetEntry';
import { getTimelineEntities } from '@acceligentllc/shared/utils/timeSheetEntry';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimelineEntityType } from '@acceligentllc/shared/enums/timelineEntityType';
import { TimeSheetEntryWorkType } from '@acceligentllc/shared/enums/timeSheetEntryWorkType';

import { DEFAULT_TIME_DURATION_MINUTE_ROUNDING_INTERVAL } from '@acceligentllc/shared/constants/value';

import type { RootState } from 'af-reducers';

import * as TimeCardActions from 'af-actions/timeCard';

import styles from './styles.module.scss';
import TimeSheetEntryDetails from './TimeSheetEntryDetails';

type Gap = {
	type: TimelineEntityType.GAP;
	entry: {
		startTime: string;
		endTime: string;
	};
};

type Entry = {
	type: TimelineEntityType.ENTRY;
	entry: TimeCardEntryVM;
};

interface WorkersDay {
	minutesByWorkType: Record<TimeSheetEntryWorkType, number>;
	minutesTotal: number;
}

interface OwnProps {
	/** format: "MM-DD-YYYY" */
	date: string;
	entries: TimeCardEntryVM[];
	overlaps: Nullable<Record<string, TimeSheetEntryUtils.OverlapMeta>>;
	rejectedTS: TimeCardRejectedTimeSheetVM[];
	timeOffset: number;
	timeZoneInUse: Nullable<string>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

// Need for styling - gray line between 2 entries that "connects" them
const areEntitiesConnected = (
	e1?: TimeSheetEntryUtils.TimelineEntity<TimeCardEntryVM, TimeSheetEntryUtils.OccupiedSlotModel>,
	e2?: TimeSheetEntryUtils.TimelineEntity<TimeCardEntryVM, TimeSheetEntryUtils.OccupiedSlotModel>
) => {
	if (!e1 || !e2 || e1.type === TimelineEntityType.GAP || e2.type === TimelineEntityType.GAP) {
		return false;
	}
	return !!e1.entry.endTime && e1.entry.endTime >= e2.entry.startTime;
};

const DayInTimeCard: React.FC<Props> = (props: Props) => {
	const {
		date,
		entries,
		overlaps,
		rejectedTS,
		timeOffset,
		timeZoneInUse,
		timeRoundingPolicyFrom,
		shouldRoundUnassignedEntries,
	} = props;

	const [shouldRoundUnassignedTo1Min, setShouldRoundUnassignedTo1Min] = React.useState<boolean>(false);

	const timelineEntities = React.useMemo(() => getTimelineEntities(entries, undefined, true), [entries]);

	const hasUnassignedEntry = React.useMemo(() => {
		return entries.some((entry) => entry.workOrderCode === null);
	}, [entries]);

	const dueDateByWoId = React.useMemo(() => {
		const recordDueDateByWoId: Record<number, string> = {};
		entries.forEach((entry) => {
			if (entry.workOrderId) {
				recordDueDateByWoId[entry.workOrderId] = entry.workOrderDueDate;
			}
		});
		return recordDueDateByWoId;
	}, [entries]);

	const fetchFlagForRoundingUnassignedEntries = React.useCallback(async () => {
		const _shouldRound = await shouldRoundUnassignedEntries();
		setShouldRoundUnassignedTo1Min(_shouldRound);
	}, [shouldRoundUnassignedEntries]);

	React.useEffect(() => {
		fetchFlagForRoundingUnassignedEntries();
	}, [fetchFlagForRoundingUnassignedEntries]);

	let infoForRejectMessage: TimeCardRejectedTimeSheetVM[] = [];

	if (rejectedTS.length) {
		infoForRejectMessage = rejectedTS;
	}

	const isToday = React.useMemo(() => date === TimeUtils.formatDate(new Date(), TimeFormat.DATE_ONLY), [date]);

	const dateToDisplay = React.useMemo(() => {
		return isToday ?
			`TODAY, ${date}` :
			TimeUtils.formatDate(date, TimeFormat.DAY_WITH_DATE, TimeFormat.DATE_ONLY);
	}, [date, isToday]);

	const workersDay: WorkersDay = React.useMemo(() => {
		const calculated: WorkersDay = {
			minutesTotal: 0,
			minutesByWorkType: {
				[TimeSheetEntryWorkType.BREAK]: 0,
				[TimeSheetEntryWorkType.JOB]: 0,
				[TimeSheetEntryWorkType.SHOP]: 0,
				[TimeSheetEntryWorkType.TRAVEL]: 0,
			},
		};
		const sumByWorkOrderIdMap: Record<number, Record<TimeSheetEntryWorkType, number>> = {};

		// For time calculation we have to exclude currently active shift
		const dateWithEntriesWithoutActive = entries.filter((_entry) => _entry.endTime);

		// Calculate times by work type
		for (const _entry of Object.values(dateWithEntriesWithoutActive)) {
			const minutes = TimeUtils.getDiff(_entry.endTime, _entry.startTime, 'minutes', TimeFormat.ISO_DATETIME);

			// If there is no workOrderId, all entries belong to same workOrder
			const workOrderId = _entry.workOrderId ?? 0;
			if (!sumByWorkOrderIdMap[workOrderId]) {
				sumByWorkOrderIdMap[workOrderId] = {
					[TimeSheetEntryWorkType.BREAK]: 0,
					[TimeSheetEntryWorkType.JOB]: 0,
					[TimeSheetEntryWorkType.SHOP]: 0,
					[TimeSheetEntryWorkType.TRAVEL]: 0,
				};
			}
			sumByWorkOrderIdMap[workOrderId][_entry.workType] += minutes;
		}

		for (const [woId, _sums] of Object.entries(sumByWorkOrderIdMap)) {
			const woDueDate = woId in dueDateByWoId ? dueDateByWoId[+woId] : null;
			let shouldRoundTo1Min = false;
			if (woDueDate) {
				shouldRoundTo1Min = shouldEnableFeatureFromDate({
					enableFeatureFrom: timeRoundingPolicyFrom,
					workOrderDueDate: woDueDate,
				});
			} else {
				shouldRoundTo1Min = shouldRoundUnassignedTo1Min;
			}
			for (const [_workType, _minutes] of Object.entries(_sums)) {
				const roundedValue = shouldRoundTo1Min
					? _minutes
					: TimeUtils.roundTimeDurationToInterval(_minutes, DEFAULT_TIME_DURATION_MINUTE_ROUNDING_INTERVAL);
				calculated.minutesByWorkType[_workType as TimeSheetEntryWorkType] += roundedValue;
				calculated.minutesTotal += _workType === TimeSheetEntryWorkType.BREAK ? 0 : roundedValue;
			}
		}
		return calculated;
	}, [entries, dueDateByWoId, shouldRoundUnassignedTo1Min, timeRoundingPolicyFrom]);

	const jobTimeTotal = React.useMemo(() => {
		return TimeUtils.minutesToHoursAndMinutes(workersDay.minutesByWorkType[TimeSheetEntryWorkType.JOB]);
	}, [workersDay.minutesByWorkType]);
	const shopTimeTotal = React.useMemo(() => {
		return TimeUtils.minutesToHoursAndMinutes(workersDay.minutesByWorkType[TimeSheetEntryWorkType.SHOP]);
	}, [workersDay.minutesByWorkType]);
	const travelTimeTotal = React.useMemo(() => {
		return TimeUtils.minutesToHoursAndMinutes(workersDay.minutesByWorkType[TimeSheetEntryWorkType.TRAVEL]);
	}, [workersDay.minutesByWorkType]);
	const breakTimeTotal = React.useMemo(() => {
		return TimeUtils.minutesToHoursAndMinutes(workersDay.minutesByWorkType[TimeSheetEntryWorkType.BREAK]);
	}, [workersDay.minutesByWorkType]);

	const totalWorkingTimeToDisplay = React.useMemo(() => {
		return TimeUtils.minutesToHoursAndMinutes(workersDay.minutesTotal);
	}, [workersDay.minutesTotal]);

	return (
		<div className={styles.timeCardDay}>
			<div className={styles.timeCardDate}>
				<div>
					{dateToDisplay.toUpperCase()}
				</div>
				{!!hasUnassignedEntry && <div className={styles.linkText}>
					Assign All
				</div>}
			</div>

			<div className={styles.totalTimeGeneral}>
				<div className={styles.firstRow}>
					<div>
						Total
					</div>
					<div className={styles.time}>
						{totalWorkingTimeToDisplay}
					</div>
				</div>
				{
					!!workersDay.minutesTotal && <div className={styles.secondRow}>
						<div className={styles.iconAndTime}>
							<span className="icon-equipment" />
							{jobTimeTotal}
						</div>
						<div className={styles.iconAndTime}>
							<span className="icon-home" />
							{shopTimeTotal}
						</div>
						<div className={styles.iconAndTime}>
							<span className="icon-travel" />
							{travelTimeTotal}
						</div>
						<div className={styles.iconAndTime}>
							<span className="icon-break" />
							{breakTimeTotal}
						</div>
					</div>
				}
			</div>

			{!!infoForRejectMessage.length && infoForRejectMessage.map((_message) => {
				return (
					<React.Fragment key={_message.workOrderCode}>
						<div className={styles.timeCardEntriesAlert}>
							Time Sheet for <b>{_message.workOrderCode}</b> of <b>{TimeUtils.formatDate(
								_message.dueDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY
							)}</b>
							{' '}has been rejected by <b>{_message.rejectedByFullName ?? 'ACS'}</b>. Reason: <b>{_message.rejectReason}</b>
						</div>
					</React.Fragment>
				);
			})}

			{/* IF THERE IS NO ENTRY FOR TODAY'S DATE, SHOW EMPTY TSE INFO */}
			{!timelineEntities.length && isToday &&
				<div className={styles.emptyEntryRow}>
					There is no logged time for today.
				</div>
			}

			{
				timelineEntities.map((_tse: Entry | Gap, index: number) => {
					const hasPredecessor = areEntitiesConnected(timelineEntities[index + 1], _tse);
					const hasSuccessor = areEntitiesConnected(_tse, timelineEntities[index - 1]);

					return (
						<React.Fragment key={`${_tse.type}-${_tse.entry.startTime}-${_tse.entry.endTime}`}>
							{/* Gaps have to be shown in time card as a warning*/}
							{_tse.type === TimelineEntityType.GAP &&
								<div className={styles.gapRow}>
									<span className="icon-info" />
									A gap in your timeline
								</div>
							}

							{/* Entry */}
							{_tse.type === TimelineEntityType.ENTRY &&
								<TimeSheetEntryDetails
									entry={_tse.entry}
									hasPredecessor={hasPredecessor}
									hasSuccessor={hasSuccessor}
									overlaps={overlaps}
									timeOffset={timeOffset}
									timeZoneInUse={timeZoneInUse}
								/>
							}
						</React.Fragment>
					);
				})
			}
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { company: { company } } = state;

	return {
		timeRoundingPolicyFrom: company?.timeRoundingPolicyFrom ?? null,
	};
}

function mapDispatchToProps() {
	return {
		shouldRoundUnassignedEntries: TimeCardActions.shouldRoundUnassignedEntries,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);
export default enhance(DayInTimeCard);
